import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, useSearchParams } from 'react-router-dom';

// --- SUB-COMPONENTS ---
import AdminPanel from './AdminPanel';
import RestaurantSettings from './RestaurantSettings';
import TableManager from './TableManager';
import OrdersPanel from './OrdersPanel';
import IncomingOrderModal from './IncomingOrderModal';
import ReviewsPanel from './ReviewsPanel';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('view') || 'menu';

  // --- CORE DATA STATE ---
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- UI STATE ---
  const [categories, setCategories] = useState([]);
  const [newRestaurantName, setNewRestaurantName] = useState('');

  // --- MODAL STATES ---
  const [showAddModal, setShowAddModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // --- INCOMING ORDERS ---
  const [incomingOrders, setIncomingOrders] = useState([]);

  // --- SUBSCRIPTION STATE ---
  const [plans, setPlans] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // --- 1. INITIAL DATA FETCH ---
  useEffect(() => {
    const checkUserAndFetch = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        navigate('/login');
        return;
      }
      setUser(session.user);

      const { data: resto } = await supabase
        .from('restaurants')
        .select('*')
        .eq('owner_id', session.user.id)
        .maybeSingle();

      if (resto) {
        setRestaurant(resto);
        fetchMenuItems(resto.id);
      } else {
        setLoading(false);
        setShowOnboarding(true);
      }

      // Fetch plans
      const { data: plansData } = await supabase.from('plans').select('*').eq('is_active', true).order('price');
      if (plansData) setPlans(plansData);
    };
    checkUserAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchMenuItems = async (restoId) => {
    const { data } = await supabase.from('menu_items').select('*').eq('restaurant_id', restoId).order('created_at', { ascending: false });
    if (data) setMenuItems(data);
    setLoading(false);
  };

  // --- 2. GLOBAL REALTIME LISTENER ---
  useEffect(() => {
    if (!restaurant?.id || !restaurant?.is_pro) return; // Only listen if PRO

    const channel = supabase
      .channel('global-orders')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders', filter: `restaurant_id=eq.${restaurant.id}` },
        (payload) => {
          setIncomingOrders(prev => [payload.new, ...prev]);
          const audio = new Audio('https://codeskulptor-demos.commondatastorage.googleapis.com/pang/pop.mp3');
          audio.play().catch(e => console.log("Audio blocked", e));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [restaurant]);

  // Update categories dynamically
  useEffect(() => {
    if (menuItems.length > 0) {
      const existingCats = new Set(menuItems.map(item => item.category));
      setCategories(Array.from(new Set(["Starters", "Main Course", "Desserts", "Drinks", ...existingCats])));
    }
  }, [menuItems]);

  // --- ACTIONS ---
  const handleProcessOrder = (orderId) => {
    setIncomingOrders(prev => prev.filter(order => order.id !== orderId));
  };

  const createRestaurant = async (e) => {
    e.preventDefault();
    if (!newRestaurantName.trim()) return;
    const { data, error } = await supabase.from('restaurants').insert([{ owner_id: user.id, name: newRestaurantName }]).select().single();
    if (!error) { setRestaurant(data); setShowOnboarding(false); }
  };

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/'); };

  // --- RAZORPAY CHECKOUT ---
  const handleUpgradeClick = async (planSlug) => {
    setPaymentLoading(true);
    try {
      // 1. Create subscription on backend
      const response = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planSlug, restaurantId: restaurant.id }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      // 2. Open Razorpay Checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        subscription_id: data.subscriptionId,
        name: 'TableAR',
        description: `${data.planName} Plan - Monthly Subscription`,
        image: '/favicon.png',
        prefill: {
          email: user?.email || '',
          name: restaurant?.name || '',
        },
        notes: {
          restaurant_name: restaurant?.name,
          plan: data.planName,
        },
        handler: async function (response) {
          // 3. Verify payment
          try {
            const verifyRes = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature,
                restaurantId: restaurant.id,
                planSlug,
              }),
            });
            if (verifyRes.ok) {
              const { data: updatedResto } = await supabase.from('restaurants').select('*').eq('id', restaurant.id).single();
              if (updatedResto) setRestaurant(updatedResto);
              setShowUpgradeModal(false);
              alert('🎉 Plan upgraded successfully! Enjoy your new features.');
            } else {
              alert('Payment received but verification failed. Contact support.');
            }
          } catch (err) {
            console.error('Verification error:', err);
            alert('Payment verification error. Contact support.');
          }
        },
        theme: { color: '#ee5b2b' },
        modal: { ondismiss: () => setPaymentLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Upgrade error:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setPaymentLoading(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item? it will also remove its associated 3D models and images.')) return;

    const item = menuItems.find(i => i.id === id);
    if (!item) return;

    try {
      // 1. Delete associated files from storage
      const getStoragePath = (url) => {
        if (!url) return null;
        const cleanUrl = url.split('?')[0]; // Remove query params
        const match = cleanUrl.match(/\/storage\/v1\/object\/public\/models\/(.+)/);
        return match ? match[1] : null;
      };

      const pathsToDelete = [];
      
      const imagePath = getStoragePath(item.image_url);
      if (imagePath) pathsToDelete.push(imagePath);

      const modelPath = getStoragePath(item.model_url);
      // Delete model ONLY if it was custom uploaded (not from the Master Admin 'library/')
      if (modelPath && !modelPath.startsWith('library/')) {
        pathsToDelete.push(modelPath);
      }

      if (pathsToDelete.length > 0) {
        await supabase.storage.from('models').remove(pathsToDelete);
      }

      // 2. Delete from database
      const { error } = await supabase.from('menu_items').delete().eq('id', id);
      if (error) throw error;

      setMenuItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Error deleting item files:', err);
      alert('Error deleting item: ' + err.message);
    }
  };

  // --- MODAL HANDLERS ---
  const openEditModal = (item) => { setItemToEdit(item); setShowAddModal(true); };
  const openAddModal = () => {
    if (!restaurant?.is_pro && menuItems.length >= 3) {
      setShowUpgradeModal(true);
      return;
    }
    setItemToEdit(null);
    setShowAddModal(true);
  };
  const handleSaveItem = (savedItem) => {
    if (itemToEdit) setMenuItems(menuItems.map(i => i.id === savedItem.id ? savedItem : i));
    else setMenuItems([savedItem, ...menuItems]);
    setShowAddModal(false); setItemToEdit(null);
  };

  // --- NAVIGATION HANDLER (LOCK LOGIC) ---
  const handleViewChange = (view) => {
    if ((view === 'orders' || view === 'reviews') && !restaurant.is_pro) {
      setShowUpgradeModal(true); // Show upgrade wall
    } else {
      setSearchParams({ view });
      setShowMobileMenu(false);
    }
  };

  const resetView = () => setSearchParams({ view: 'menu' });

  if (loading) return <div className="h-screen flex items-center justify-center font-bold text-gray-500">Loading Dashboard...</div>;

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-gray-900">

      {/* GLOBAL POPUP (Only for Pro) */}
      {incomingOrders.length > 0 && restaurant?.is_pro && (
        <IncomingOrderModal orders={incomingOrders} onProcessOrder={handleProcessOrder} onCloseAll={() => setIncomingOrders([])} />
      )}

      {/* UPGRADE MODAL — Premium */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full relative overflow-hidden max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowUpgradeModal(false)} className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-black hover:bg-white font-bold z-10 text-lg shadow-sm border border-gray-100 transition-all">✕</button>

            {/* Header */}
            <div className="relative bg-gradient-to-br from-orange-500 via-red-500 to-purple-600 px-8 pt-10 pb-12 text-white text-center overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">✨ Special Offer</div>
                <h2 className="text-3xl md:text-4xl font-black mb-3 tracking-tight">Supercharge Your Menu</h2>
                <p className="text-white/80 text-sm max-w-md mx-auto leading-relaxed">Join 100+ restaurants already using TableAR Pro to boost engagement and sales.</p>
              </div>
            </div>

            {/* Quick Benefits */}
            <div className="flex flex-wrap justify-center gap-3 -mt-5 px-6 relative z-10">
              {['📱 Live Orders', '🎨 Custom Branding', '📊 Analytics', '🚀 Unlimited Items'].map((b, i) => (
                <span key={i} className="bg-white px-4 py-2 rounded-full text-xs font-bold text-gray-700 shadow-md border border-gray-100">{b}</span>
              ))}
            </div>

            {/* Plan Cards */}
            <div className="p-6 pt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {plans.map(plan => {
                const isGrowth = plan.slug === 'growth';
                const isUltimate = plan.slug === 'ultimate';
                const isCurrent = (isGrowth && restaurant?.is_pro && !restaurant?.is_ultimate) || (isUltimate && restaurant?.is_ultimate);

                return (
                  <div key={plan.id} className={`relative rounded-2xl p-6 transition-all hover:shadow-xl hover:-translate-y-1 ${isUltimate
                      ? 'bg-gradient-to-b from-gray-900 to-gray-800 text-white border-2 border-purple-500/50 shadow-lg shadow-purple-500/10'
                      : 'bg-white border-2 border-orange-200 shadow-md hover:border-orange-400'
                    }`}>
                    {/* Badge */}
                    {isGrowth && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-black px-4 py-1 rounded-full uppercase tracking-widest shadow-lg">⭐ Most Popular</span>
                      </div>
                    )}
                    {isUltimate && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[10px] font-black px-4 py-1 rounded-full uppercase tracking-widest shadow-lg">💎 Best Value</span>
                      </div>
                    )}

                    <div className="text-center mt-2 mb-5">
                      <h3 className={`text-sm font-black uppercase tracking-widest mb-3 ${isUltimate ? 'text-purple-400' : 'text-orange-500'
                        }`}>{plan.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className={`text-5xl font-black ${isUltimate ? 'text-white' : 'text-gray-900'}`}>₹{plan.price}</span>
                        <span className={`text-sm ${isUltimate ? 'text-gray-400' : 'text-gray-400'}`}>/mo</span>
                      </div>
                    </div>

                    <div className={`h-px mb-5 ${isUltimate ? 'bg-white/10' : 'bg-gray-100'}`} />

                    <ul className="space-y-3 mb-6">
                      {(plan.features || []).map((f, i) => (
                        <li key={i} className={`flex items-start gap-2.5 text-sm font-medium ${isUltimate ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                          <span className={`mt-0.5 text-xs font-bold flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${isUltimate ? 'bg-purple-500/20 text-purple-400' : 'bg-green-100 text-green-600'
                            }`}>✓</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleUpgradeClick(plan.slug)}
                      disabled={paymentLoading || isCurrent}
                      className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed ${isCurrent
                          ? (isUltimate ? 'bg-white/10 text-white/60 border border-white/10' : 'bg-gray-100 text-gray-400 border border-gray-200')
                          : isUltimate
                            ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40'
                            : 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40'
                        }`}
                    >
                      {paymentLoading ? (
                        <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Processing...</span>
                      ) : isCurrent ? '✅ Current Plan' : `Get ${plan.name} →`}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Trust Footer */}
            <div className="px-6 pb-6 pt-2 flex flex-col items-center gap-2">
              <div className="flex items-center gap-4 text-gray-400">
                <span className="text-xs font-medium">🔒 Secured by Razorpay</span>
                <span className="text-gray-200">|</span>
                <span className="text-xs font-medium">↩️ Cancel anytime</span>
                <span className="text-gray-200">|</span>
                <span className="text-xs font-medium">📧 Invoice on email</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ONBOARDING */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
            <h2 className="text-2xl font-bold mb-2">Name Your Restaurant</h2>
            <form onSubmit={createRestaurant}>
              <input autoFocus type="text" className="w-full p-3 border rounded-xl mb-4" value={newRestaurantName} onChange={(e) => setNewRestaurantName(e.target.value)} />
              <button className="w-full py-3 bg-[#ee5b2b] text-white font-bold rounded-xl">Create</button>
            </form>
          </div>
        </div>
      )}

      {/* MOBILE HEADER */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-40">
        <div className="flex items-center gap-2">
          {restaurant?.logo_url ? <img src={restaurant.logo_url} alt="" className="w-8 h-8 object-contain" /> : <img src="/favicon.png" alt="" className="w-8 h-8 object-contain" />}
          <span className="font-bold truncate">{restaurant?.name || 'Dashboard'}</span>
        </div>
        <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="text-2xl p-2 focus:outline-none">
          {showMobileMenu ? '✕' : '☰'}
        </button>
      </div>

      {/* SIDEBAR - Responsive */}
      <aside className={`w-64 bg-white border-r border-gray-200 flex-col fixed inset-y-0 left-0 z-50 transition-transform duration-300 transform md:translate-x-0 md:static md:h-screen ${showMobileMenu ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-gray-100 flex items-center gap-2">
          {restaurant?.logo_url ? <img src={restaurant.logo_url} alt="" className="w-8 h-8 object-contain" /> : <img src="/favicon.png" alt="" className="w-8 h-8 object-contain" />}
          <div className="overflow-hidden">
            <span className="font-bold truncate block">{restaurant?.name || 'Dashboard'}</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${restaurant?.is_ultimate ? 'bg-gradient-to-r from-gray-900 via-purple-900 to-violet-900 text-white border border-purple-500/30' : (restaurant?.is_pro ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' : 'bg-gray-100 text-gray-500')}`}>
              {restaurant?.is_ultimate ? 'ULTIMATE PLAN' : (restaurant?.is_pro ? 'PRO PLAN' : 'FREE PLAN')}
            </span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <div onClick={() => handleViewChange('menu')} className={`px-4 py-3 rounded-lg font-bold flex items-center gap-3 cursor-pointer ${activeView === 'menu' ? 'bg-orange-50 text-[#ee5b2b]' : 'text-gray-500 hover:bg-gray-50'}`}>
            <span>📋</span> Menu Items
          </div>

          <div onClick={() => handleViewChange('orders')} className={`px-4 py-3 rounded-lg font-bold flex items-center justify-between cursor-pointer ${activeView === 'orders' ? 'bg-orange-50 text-[#ee5b2b]' : 'text-gray-500 hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><span>🔔</span> Live Orders</div>
            {!restaurant?.is_pro && <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded text-gray-500">🔒</span>}
          </div>

          <div className="h-px bg-gray-100 my-2 mx-4"></div>

          <div onClick={() => handleViewChange('tables')} className={`px-4 py-3 rounded-lg font-medium flex items-center gap-3 cursor-pointer ${activeView === 'tables' ? 'bg-orange-50 text-[#ee5b2b] font-bold' : 'text-gray-500 hover:bg-gray-50'}`}>
            <span>🪑</span> Tables & QR
          </div>
          <div onClick={() => handleViewChange('reviews')} className={`px-4 py-3 rounded-lg font-medium flex items-center justify-between cursor-pointer ${activeView === 'reviews' ? 'bg-orange-50 text-[#ee5b2b] font-bold' : 'text-gray-500 hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><span>⭐</span> Feedback & Ratings</div>
            {!restaurant?.is_pro && <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded text-gray-500">🔒</span>}
          </div>
          <div onClick={() => { navigate('/analytics'); setShowMobileMenu(false); }} className="text-gray-500 hover:bg-gray-50 px-4 py-3 rounded-lg font-medium flex items-center gap-3 cursor-pointer hover:text-[#ee5b2b]">
            <span>📊</span> Analytics
          </div>
          <div onClick={() => handleViewChange('settings')} className={`px-4 py-3 rounded-lg font-medium flex items-center gap-3 cursor-pointer ${activeView === 'settings' ? 'bg-orange-50 text-[#ee5b2b] font-bold' : 'text-gray-500 hover:bg-gray-50'}`}>
            <span>⚙️</span> Settings
          </div>
        </nav>

        {/* TEMPTING UPGRADE CTA — Only for Free Users */}
        {!restaurant?.is_pro && (
          <div className="mx-3 mb-3">
            <div
              onClick={() => setShowUpgradeModal(true)}
              className="relative overflow-hidden rounded-2xl p-4 cursor-pointer group transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #ee5b2b 0%, #d94a1a 50%, #c2185b 100%)' }}
            >
              {/* Animated shimmer */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">⚡</span>
                  <span className="text-white font-black text-sm uppercase tracking-wide">Upgrade to Pro</span>
                </div>
                <p className="text-white/80 text-xs leading-relaxed mb-3">Unlock Live Orders, Analytics, Custom Branding & more</p>
                <div className="flex items-center gap-2">
                  <span className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-xs font-black shadow-md">From ₹599/mo</span>
                  <span className="text-white/60 text-xs">→</span>
                  <span className="text-white/90 text-xs font-bold animate-pulse">Limited Time</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 border-t"><button onClick={handleLogout} className="w-full text-left text-gray-500 hover:text-red-500 font-bold text-sm px-4">🚪 Sign Out</button></div>
      </aside>

      {/* OVERLAY for Mobile Menu */}
      {showMobileMenu && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setShowMobileMenu(false)}></div>}

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto pt-16 md:pt-0">
        {/* REVIEWS VIEW (New) */}
        {activeView === 'reviews' && restaurant?.is_pro ? (
          <div className="p-4 md:p-8">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-2xl font-bold">Customer Reviews</h1>
                <p className="text-gray-500 text-sm">See what your customers are saying</p>
              </div>
            </header>
            <ReviewsPanel restaurantId={restaurant.id} />
          </div>
        ) : activeView === 'orders' && restaurant?.is_pro ? (
          <OrdersPanel restaurantId={restaurant?.id} />
        ) : (
          /* DEFAULT: MENU ITEMS VIEW (Only show if not in other views) */
          (activeView === 'menu' || (!['orders', 'reviews', 'tables', 'settings'].includes(activeView))) && (
            <div className="p-4 md:p-8">
              <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-2xl font-bold">Menu Management</h1>
                  <p className="text-gray-500 text-sm">Manage your menu and AR assets</p>
                </div>
                <button onClick={openAddModal} className="w-full md:w-auto bg-[#ee5b2b] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#d94a1a] shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2">
                  <span>+</span> Add New Item
                </button>
              </header>

              {/* ITEM LIST - RESPONSIVE (Cards on Mobile, Table on Desktop) */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

                {/* DESKTOP TABLE HEADER */}
                <div className="hidden md:grid grid-cols-12 bg-gray-50 border-b border-gray-100 p-4 text-xs font-bold text-gray-500 uppercase">
                  <div className="col-span-1">Image</div>
                  <div className="col-span-4">Name</div>
                  <div className="col-span-3">Category</div>
                  <div className="col-span-2">Price</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                <div className="divide-y divide-gray-50">
                  {menuItems.map(item => (
                    <div key={item.id} className="p-4 flex flex-col md:grid md:grid-cols-12 items-start md:items-center gap-4 hover:bg-gray-50 transition-colors">

                      {/* Image & Mobile Header */}
                      <div className="col-span-1 flex items-center gap-4 w-full md:w-auto">
                        <img src={item.image_url || 'https://via.placeholder.com/40'} className="w-16 h-16 md:w-10 md:h-10 rounded-lg object-cover bg-gray-100 border border-gray-200" alt="" />

                        {/* Mobile Only Details */}
                        <div className="md:hidden flex-1">
                          <div className="font-bold text-gray-900 text-lg">{item.name}</div>
                          <div className="text-sm text-gray-500">{item.category}</div>
                          <div className="font-bold text-[#ee5b2b] mt-1">
                            {item.variants?.length > 0 ? `From ₹${Math.min(...item.variants.map(v => parseFloat(v.price) || 0))}` : `₹${item.price}`}
                          </div>
                        </div>
                      </div>

                      {/* Desktop Columns */}
                      <div className="col-span-4 font-bold text-gray-900 hidden md:block">
                        {item.name} {item.is_todays_special && '🔥'} {!item.is_available && '🚫'}
                      </div>
                      <div className="col-span-3 text-sm text-gray-500 hidden md:block">{item.category}</div>
                      <div className="col-span-2 font-bold hidden md:block">
                        {item.variants?.length > 0 ? `From ₹${Math.min(...item.variants.map(v => parseFloat(v.price) || 0))}` : `₹${item.price}`}
                      </div>

                      {/* Actions (Responsive) */}
                      <div className="col-span-2 flex items-center justify-end gap-3 w-full md:w-auto mt-2 md:mt-0 border-t pt-3 md:border-0 md:pt-0 border-gray-100">
                        <button onClick={() => openEditModal(item)} className="flex-1 md:flex-none py-2 md:py-0 text-center text-gray-600 hover:text-blue-600 font-bold text-sm bg-gray-100 md:bg-transparent rounded-lg">Edit</button>
                        <button onClick={() => deleteItem(item.id)} className="flex-1 md:flex-none py-2 md:py-0 text-center text-gray-600 hover:text-red-600 font-bold text-sm bg-gray-100 md:bg-transparent rounded-lg">Delete</button>
                      </div>

                    </div>
                  ))}
                </div>

                {menuItems.length === 0 && <div className="p-10 text-center text-gray-400">No items found. Tap "Add New Item" to start!</div>}
              </div>
            </div>
          )
        )}
      </main>

      {/* MODALS controlled by URL param or shown on top */}
      {showAddModal && <AdminPanel categories={categories} onAddItem={handleSaveItem} onCancel={() => setShowAddModal(false)} restaurantId={restaurant.id} itemToEdit={itemToEdit} />}

      {/* Settings & Tables are now 'Views' via URL, but we need to render them if activeView matches */}
      {activeView === 'settings' && <RestaurantSettings restaurant={restaurant} onClose={resetView} onUpdate={(data) => setRestaurant(data)} />}
      {activeView === 'tables' && <TableManager restaurant={restaurant} onClose={resetView} />}

    </div>
  );
};

export default Dashboard;